import React, { useState } from 'react';
import { Button, Spin } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import type { AgentWidgetProps } from 'lib.components/src/components/AgentWidgets';
import { useAuth } from 'lib.context/src/AuthContext';
import { ORGANIZATION_TYPE } from 'lib.store/src/main/organization/organizationType';
import { useLazyGetMatchQuery } from 'lib.store/src/type-attendee/match/match.api';
import type { PartnerMatchDetails } from 'lib.store/src/type-attendee/match/models';
import { TextBodyRegular } from 'lib.typography';
import { MatchStatuses } from 'lib.shared/src/constants';

import { MatchReasoningDetails, hasReasoningDetails, stripEmphasis } from '../../MatchReasoning';
import { PartnerInitial } from '../../../pages/AttendeeRolePages/Matches/components/DeckCard';
import './styles.scss';

/**
 * A recorded match decision in an agent feed: the company row marked
 * Skipped/Shortlisted.
 *
 * The row itself is rendered from the message's own meta — the decision is
 * history and must not depend on a list request. Why the company was matched is
 * a click away: expanding fetches THAT card (`GET /attendee/matches/{id}`),
 * which is one row instead of the deck the feed used to download to find it.
 */
const MatchDecisionWidget: React.FC<AgentWidgetProps> = ({ meta }) => {
  const { user } = useAuth();
  // the widget registry is app-wide, so an admin inspecting an attendee thread
  // renders this too — the card endpoint is attendee-only, so only they are
  // offered the expansion
  const canOpenCard = user?.organization?.type === ORGANIZATION_TYPE.attendee;

  const how = meta.decision === MatchStatuses.passed ? 'skipped' : 'kept';

  // the partner identity block the card renders, stored with the decision;
  // rows recorded before it carry a flat company name
  const partner = (meta.partner ?? null) as { name?: string | null; logo?: string | null } | null;
  const name = partner?.name ?? (meta.company as string | undefined) ?? 'Unknown company';
  const matchId = typeof meta.match_id === 'number' ? meta.match_id : null;

  const [isOpen, setOpen] = useState(false);
  const [card, setCard] = useState<PartnerMatchDetails | null>(null);
  const [fetchCard, { isFetching }] = useLazyGetMatchQuery();

  const toggle = async () => {
    if (isOpen) {
      setOpen(false);
      return;
    }

    setOpen(true);

    if (card || !matchId) return;

    try {
      const response = await fetchCard({ id: matchId }).unwrap();
      setCard(response.data);
    } catch {
      // the card is gone (or not ours any more) — the row still says what
      // was decided, which is what this message is about
      setOpen(false);
    }
  };

  return (
    <div className="match-decision-widget">
      <div className="match-decision-widget__row">
        <PartnerInitial name={name} logo={partner?.logo ?? null} />
        <TextBodyRegular className="text-color-neutral-grey-10 match-decision-widget__name">
          {name}
        </TextBodyRegular>
        <span className={`match-deck-card__verdict match-deck-card__verdict--${how}`}>
          {how === 'kept' ? 'Shortlisted' : 'Skipped'}
        </span>
        {matchId && canOpenCard && (
          <Button
            className="match-decision-widget__toggle"
            type="text"
            size="small"
            icon={<RightOutlined rotate={isOpen ? 90 : 0} />}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Hide details' : 'Show details'}
            title={isOpen ? 'Hide details' : 'Show details'}
            onClick={() => void toggle()}
            data-test-id="match-decision-toggle"
          />
        )}
      </div>
      {isOpen && (
        <div className="match-decision-widget__body">
          {isFetching && !card ? (
            <Spin size="small" />
          ) : (
            card && (
              <>
                {card.reasoning?.summary && (
                  <TextBodyRegular className="match-deck-card__summary">
                    {stripEmphasis(card.reasoning.summary)}
                  </TextBodyRegular>
                )}
                {hasReasoningDetails(card) && <MatchReasoningDetails match={card} />}
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MatchDecisionWidget;
